import { NgTemplateOutlet } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	model,
	output,
} from '@angular/core';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import type { DropdownOption } from '@valk-nx/components/ui-select-shell/src/lib/select-shell.interface';
import { TagComponent } from '@valk-nx/components/ui-tag/src/lib/tag';
import { FilterMapButtonComponent } from '@valk-nx/compositions/ui-filters/src/lib/components/filter-map-button/filter-map-button';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';
import { injectTranslationKeyExists } from '@valk-nx/helpers/lib/translation/translation-key-exists';

import { FilterCheckboxGroupComponent } from './components/filter-checkbox-group/filter-checkbox-group';
import { FilterLayoutComponent } from './components/filter-layout/filter-layout';
import { FilterNumericComponent } from './components/filter-numeric/filter-numeric';
import { FilterRadioGroupComponent } from './components/filter-radio-group/filter-radio-group';
import { FilterSearchComponent } from './components/filter-search/filter-search';
import type {
	FiltersInterface,
	FiltersItemInterface,
} from './contracts/filters.interface';
import {
	SINGLE_VALUE_FILTER_TYPES,
	SUPPORTED_FILTER_TYPES,
} from './contracts/filters.interface';

interface FilterChipsInterface {
	group: string;
	name: string;
	value: string;
	withGroupLabel: boolean;
}

const GROUP_TRANSLATION_PREFIX = 'compositions.filters.type';

/** Shared by every filter group type that collapses long item lists. */
export const ITEMS_LIMIT = 8;

/**
 * Chip group of the searched location. Not a key of `activeFilters`: the
 * location is request state the parent owns, so removing its chip is
 * reported through `searchCleared` instead of `activeFiltersChange`.
 */
export const SEARCH_LOCATION_GROUP = 'search-location';

@Component({
	selector: `vp-filters`,
	templateUrl: './filters.component.html',
	imports: [
		FilterCheckboxGroupComponent,
		FilterLayoutComponent,
		FilterNumericComponent,
		FilterRadioGroupComponent,
		FilterSearchComponent,
		NgTemplateOutlet,
		TagComponent,
		TranslatePipe,
		SVG_ICON_IMPORTS,
		FilterMapButtonComponent,
	],
})
export class FiltersComponent {
	protected readonly ITEMS_LIMIT = ITEMS_LIMIT;

	private readonly translate = inject(TranslateService);
	private readonly translationKeyExists = injectTranslationKeyExists();

	filters = input<FiltersInterface[]>([]);
	searchOptions = input<DropdownOption[]>([]);
	searchLoading = input<boolean>(false);
	searchLocation = input<string | null>(null);
	showMapButton = input(true);

	activeFilters = model<Record<string, string[]>>({});

	searched = output<string>();
	searchSelected = output<string>();
	searchCleared = output<void>();
	showMap = output<void>();

	activeFilterChips = computed<FilterChipsInterface[]>(() => {
		const active = this.activeFilters();
		const groups = this.filters();
		const location = this.searchLocation();

		const locationChips: FilterChipsInterface[] = location
			? [
					{
						group: SEARCH_LOCATION_GROUP,
						name: location,
						value: location,
						withGroupLabel: false,
					},
				]
			: [];

		const filterChips = Object.entries(active).flatMap(
			([groupTitle, values]) => {
				const group = groups.find((g) => g.title === groupTitle);
				const items = group?.items ?? [];

				return values.map((value) => {
					const item = items.find((i) => i.value === value);
					const formatted = this.formatItem(
						groupTitle,
						value,
						item?.name,
					);

					const hasReadableName = !!item && item.name !== item.value;

					return {
						group: groupTitle,
						name: formatted ?? item?.name ?? value,
						value,
						withGroupLabel:
							!formatted && !!group && !hasReadableName,
					};
				});
			},
		);

		return [...locationChips, ...filterChips];
	});

	supportedFilters = computed<FiltersInterface[]>(() => {
		return this.filters().filter((f) =>
			SUPPORTED_FILTER_TYPES.includes(f.type),
		);
	});

	filtersWithItemLabels = computed<FiltersInterface[]>(() => {
		return this.supportedFilters().map((group) => ({
			...group,
			items: group.items?.map((item) =>
				this.labelItem(group.title, item),
			),
		}));
	});

	activeSingleValueByGroup = computed<Record<string, string>>(() => {
		const active = this.activeFilters();

		return Object.fromEntries(
			this.supportedFilters()
				.filter((group) =>
					SINGLE_VALUE_FILTER_TYPES.includes(group.type),
				)
				.map((group) => [group.title, active[group.title]?.[0] ?? '']),
		);
	});

	onSearched(term: string): void {
		this.searched.emit(term);
	}

	private labelItem(
		group: string,
		item: FiltersItemInterface,
	): FiltersItemInterface {
		const formatted = this.formatItem(group, item.value, item.name);
		return formatted ? { ...item, name: formatted } : item;
	}

	/** Undefined when the group's Tolgee entry has no item format. */
	private formatItem(
		group: string,
		value: string,
		name?: string,
	): string | undefined {
		const form = Number(value) === 1 ? 'singular' : 'plural';
		const key = `${GROUP_TRANSLATION_PREFIX}.${group}.item-${form}`;
		if (!this.translationKeyExists(key)) return undefined;

		return this.translate.instant(key, { value, name }) as string;
	}

	onSearchSelected(mapboxId: string): void {
		this.searchSelected.emit(mapboxId);
	}

	clearAllFilters(): void {
		this.activeFilters.set({});
		if (this.searchLocation()) {
			this.searchCleared.emit();
		}
	}

	removeFilter(groupTitle: string, value: string): void {
		if (groupTitle === SEARCH_LOCATION_GROUP) {
			this.searchCleared.emit();
			return;
		}

		const groupValues = this.activeFilters()[groupTitle] ?? [];

		this.setGroupValues(
			groupTitle,
			groupValues.filter((v) => v !== value),
		);
	}

	setSingleValue(groupTitle: string, value: string): void {
		this.setGroupValues(groupTitle, value ? [value] : []);
	}

	setGroupValues(groupTitle: string, values: string[]): void {
		this.activeFilters.update((current) => {
			const next = { ...current };

			if (values.length > 0) {
				next[groupTitle] = values;
			} else {
				delete next[groupTitle];
			}

			return next;
		});
	}
}
